import { forwardRef, useId, type ReactNode, type MouseEvent } from 'react'
import * as C from '@radix-ui/react-checkbox'
import * as R from '@radix-ui/react-radio-group'
import * as S from '@radix-ui/react-switch'
import { Check, Minus } from 'lucide-react'
import { cn } from './cn'
import { useFieldControl } from './field'

export interface CheckboxProps {
  checked: boolean | 'indeterminate'
  onCheckedChange?: (checked: boolean) => void
  /** Raw click (DataTable uses it to read Shift for range selection). */
  onClick?: (e: MouseEvent<HTMLButtonElement>) => void
  label?: ReactNode
  description?: ReactNode
  disabled?: boolean
  id?: string
  name?: string
  className?: string
  'aria-label'?: string
}

/**
 * Checkbox with optional label/description. `indeterminate` shows a dash (e.g. "some rows selected").
 * `<Checkbox checked={v} onCheckedChange={setV} label="Track quantity" />`
 */
export const Checkbox = forwardRef<HTMLButtonElement, CheckboxProps>(function Checkbox(
  { checked, onCheckedChange, onClick, label, description, disabled, id, name, className, ...aria }, ref,
) {
  const auto = useId()
  const f = useFieldControl()
  const cid = id ?? (label ? `cb${auto.replace(/:/g, '')}` : f?.id)
  const box = (
    <C.Root
      ref={ref}
      id={cid}
      name={name}
      checked={checked}
      disabled={disabled}
      onClick={onClick}
      onCheckedChange={v => onCheckedChange?.(v === true)}
      aria-label={aria['aria-label']}
      aria-describedby={description ? `${cid}-d` : f?.describedBy}
      className={cn(
        'peer grid size-4 shrink-0 place-items-center rounded-[4px] border border-input bg-surface shadow-control transition-colors duration-100',
        'hover:border-border-strong data-[state=checked]:border-primary data-[state=checked]:bg-primary data-[state=indeterminate]:border-primary data-[state=indeterminate]:bg-primary',
        'text-primary-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus disabled:cursor-not-allowed disabled:opacity-50',
        !label && className,
      )}
    >
      <C.Indicator className="grid place-items-center data-[state=checked]:animate-pop-in">
        {checked === 'indeterminate' ? <Minus className="size-3" strokeWidth={3} /> : <Check className="size-3" strokeWidth={3} />}
      </C.Indicator>
    </C.Root>
  )
  if (!label) return box
  return (
    <div className={cn('flex items-start gap-2', className)}>
      <div className="flex h-5 items-center">{box}</div>
      <div className="flex min-w-0 flex-col">
        <label htmlFor={cid} className={cn('cursor-pointer text-base text-text select-none', disabled && 'cursor-not-allowed opacity-60')}>{label}</label>
        {description && <p id={`${cid}-d`} className="text-sm text-text-muted">{description}</p>}
      </div>
    </div>
  )
})

export interface RadioOption<V extends string = string> { value: V; label: ReactNode; description?: ReactNode; disabled?: boolean; icon?: ReactNode }

export interface RadioGroupProps<V extends string = string> {
  value: V | null
  onValueChange: (v: V) => void
  options: RadioOption<V>[]
  /** "list" = classic radios, "cards" = selectable tiles (payment method, plan interval). */
  variant?: 'list' | 'cards'
  orientation?: 'vertical' | 'horizontal'
  disabled?: boolean
  name?: string
  className?: string
  'aria-label'?: string
}

/** Radio group: `<RadioGroup value={v} onValueChange={setV} options={[{value:'a',label:'A'}]} />`. */
export function RadioGroup<V extends string = string>({ value, onValueChange, options, variant = 'list', orientation = 'vertical', disabled, name, className, ...aria }: RadioGroupProps<V>) {
  const auto = useId().replace(/:/g, '')
  const f = useFieldControl()
  return (
    <R.Root
      value={value ?? ''}
      onValueChange={v => onValueChange(v as V)}
      disabled={disabled}
      name={name}
      orientation={orientation}
      aria-label={aria['aria-label']}
      aria-describedby={f?.describedBy}
      className={cn(
        variant === 'cards' ? 'grid gap-2' : 'flex gap-3',
        variant === 'cards' ? (orientation === 'horizontal' ? 'sm:grid-flow-col sm:auto-cols-fr' : '') : orientation === 'vertical' ? 'flex-col' : 'flex-row flex-wrap gap-x-5',
        className,
      )}
    >
      {options.map(o => {
        const id = `r${auto}-${o.value}`
        if (variant === 'cards') {
          return (
            <label key={o.value} htmlFor={id}
              className={cn(
                'relative flex cursor-pointer items-start gap-3 rounded-card border border-border bg-surface p-3 transition-[border-color,box-shadow,background-color] duration-150',
                'hover:border-border-strong has-[[data-state=checked]]:border-primary has-[[data-state=checked]]:bg-primary-soft/40 has-[[data-state=checked]]:ring-1 has-[[data-state=checked]]:ring-primary',
                'has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-focus',
                o.disabled && 'cursor-not-allowed opacity-50',
              )}>
              <span className="flex h-5 shrink-0 items-center"><R.Item id={id} value={o.value} disabled={o.disabled} className="peer grid size-4 shrink-0 place-items-center rounded-full border border-input bg-surface outline-none data-[state=checked]:border-primary data-[state=checked]:bg-primary">
                <R.Indicator className="size-1.5 rounded-full bg-white" />
              </R.Item></span>
              {o.icon && <span className="text-text-muted [&_svg]:size-5">{o.icon}</span>}
              <span className="flex min-w-0 flex-col">
                <span className="text-base font-medium text-text">{o.label}</span>
                {o.description && <span className="text-sm text-text-muted">{o.description}</span>}
              </span>
            </label>
          )
        }
        return (
          <div key={o.value} className="flex items-start gap-2">
            <div className="flex h-5 items-center">
              <R.Item id={id} value={o.value} disabled={o.disabled}
                className="grid size-4 shrink-0 place-items-center rounded-full border border-input bg-surface shadow-control hover:border-border-strong data-[state=checked]:border-primary data-[state=checked]:bg-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus disabled:opacity-50">
                <R.Indicator className="size-1.5 rounded-full bg-white data-[state=checked]:animate-pop-in" />
              </R.Item>
            </div>
            <div className="flex min-w-0 flex-col">
              <label htmlFor={id} className={cn('cursor-pointer text-base text-text select-none', o.disabled && 'cursor-not-allowed opacity-60')}>{o.label}</label>
              {o.description && <p className="text-sm text-text-muted">{o.description}</p>}
            </div>
          </div>
        )
      })}
    </R.Root>
  )
}

export interface SwitchProps {
  checked: boolean
  onCheckedChange: (v: boolean) => void
  label?: ReactNode
  description?: ReactNode
  disabled?: boolean
  size?: 'sm' | 'md'
  id?: string
  /** Label on the left, switch on the right (settings rows). */
  labelPosition?: 'left' | 'right'
  className?: string
  'aria-label'?: string
}

/** On/off toggle that applies immediately (use Checkbox inside forms that save later). */
export const Switch = forwardRef<HTMLButtonElement, SwitchProps>(function Switch(
  { checked, onCheckedChange, label, description, disabled, size = 'md', id, labelPosition = 'right', className, ...aria }, ref,
) {
  const auto = useId()
  const f = useFieldControl()
  const cid = id ?? (label ? `sw${auto.replace(/:/g, '')}` : f?.id)
  const sw = (
    <S.Root
      ref={ref}
      id={cid}
      checked={checked}
      onCheckedChange={onCheckedChange}
      disabled={disabled}
      aria-label={aria['aria-label']}
      aria-describedby={description ? `${cid}-d` : f?.describedBy}
      className={cn(
        'relative inline-flex shrink-0 items-center rounded-full border border-transparent bg-border-strong transition-colors duration-150',
        'data-[state=checked]:bg-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus disabled:cursor-not-allowed disabled:opacity-50',
        'dark:bg-[#3a3a44] dark:data-[state=checked]:bg-primary',
        size === 'md' ? 'h-5 w-9' : 'h-4 w-7',
        !label && className,
      )}
    >
      <S.Thumb className={cn(
        'pointer-events-none block rounded-full bg-white shadow-[0_1px_2px_rgb(0_0_0/0.2)] transition-transform duration-150 ease-out-soft',
        size === 'md' ? 'size-4 translate-x-0.5 data-[state=checked]:translate-x-[18px]' : 'size-3 translate-x-0.5 data-[state=checked]:translate-x-[14px]',
      )} />
    </S.Root>
  )
  if (!label) return sw
  return (
    <div className={cn('flex items-start gap-3', labelPosition === 'left' && 'flex-row-reverse justify-between', className)}>
      <div className="flex h-5 items-center">{sw}</div>
      <div className="flex min-w-0 flex-col">
        <label htmlFor={cid} className={cn('cursor-pointer text-base text-text select-none', disabled && 'cursor-not-allowed opacity-60')}>{label}</label>
        {description && <p id={`${cid}-d`} className="text-sm text-text-muted">{description}</p>}
      </div>
    </div>
  )
})
